import { Component, Input, OnInit, ViewEncapsulation } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";

// Import the types from the previous artifact
export type TextFormat =
  | "bold"
  | "italic"
  | "underline"
  | "strikethrough"
  | "code";

export interface TextNode {
  type: "text";
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  code?: boolean;
}

export interface LinkNode {
  type: "link";
  url: string;
  children: Array<TextNode>;
}

export type InlineNode = TextNode | LinkNode;

export interface ParagraphNode {
  type: "paragraph";
  children: Array<InlineNode>;
}

export interface HeadingNode {
  type: "heading";
  level: 1 | 2 | 3 | 4 | 5 | 6;
  children: Array<InlineNode>;
}

export interface QuoteNode {
  type: "quote";
  children: Array<InlineNode>;
}

export interface CodeBlockNode {
  type: "code";
  children: Array<TextNode>;
}

export interface ListItemNode {
  type: "list-item";
  children: Array<InlineNode>;
}

export interface ListNode {
  type: "list";
  format: "ordered" | "unordered";
  children: Array<ListItemNode>;
}

export interface StrapiImage {
  name: string;
  alternativeText?: string;
  url: string;
  caption?: string;
  width: number;
  height: number;
  formats?: Record<string, any>;
  hash: string;
  ext: string;
  mime: string;
  size: number;
  previewUrl?: string;
  provider: string;
  provider_metadata?: any;
  createdAt: string;
  updatedAt: string;
}

export interface ImageNode {
  type: "image";
  image: StrapiImage;
  children: Array<{ type: "text"; text: string }>;
}

export type BlockNode =
  | ParagraphNode
  | HeadingNode
  | QuoteNode
  | CodeBlockNode
  | ListNode
  | ImageNode;

export type StrapiRichTextBlock = Array<BlockNode>;

// Helper type for the complete Strapi field structure
export interface StrapiRichTextField {
  id?: number;
  __component?: string;
  content?: StrapiRichTextBlock;
}

// Type guard functions
export function isTextNode(node: any): node is TextNode {
  return node?.type === "text";
}

export function isLinkNode(node: any): node is LinkNode {
  return node?.type === "link";
}

export function isParagraphNode(node: any): node is ParagraphNode {
  return node?.type === "paragraph";
}

export function isHeadingNode(node: any): node is HeadingNode {
  return node?.type === "heading";
}

export function isListNode(node: any): node is ListNode {
  return node?.type === "list";
}

export function isImageNode(node: any): node is ImageNode {
  return node?.type === "image";
}

@Component({
  selector: "app-strapi-rich-text",
  templateUrl: "./strapi-rich-text.component.html",
  styleUrls: ["./strapi-rich-text.component.sass"],
  encapsulation: ViewEncapsulation.None,
})
export class StrapiRichTextComponent implements OnInit {
  @Input() data: StrapiRichTextBlock = [];

  renderedContent: SafeHtml = "";

  constructor(private sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    this.renderContent();
  }

  ngOnChanges(): void {
    this.renderContent();
  }

  private renderContent(): void {
    const html = this.renderBlocks(this.data);
    this.renderedContent = this.sanitizer.sanitize(1, html) || "";
  }

  private renderBlocks(blocks: BlockNode[]): string {
    if (!blocks || !Array.isArray(blocks)) {
      return "";
    }
    return blocks.map((block) => this.renderBlock(block)).join("");
  }

  private renderBlock(block: BlockNode): string {
    switch (block.type) {
      case "paragraph":
        return this.renderParagraph(block);
      case "heading":
        return this.renderHeading(block);
      case "quote":
        return this.renderQuote(block);
      case "code":
        return this.renderCodeBlock(block);
      case "list":
        return this.renderList(block);
      case "image":
        return this.renderImage(block);
      default:
        return "";
    }
  }

  private renderParagraph(node: ParagraphNode): string {
    const content = this.renderInlineNodes(node.children);
    return `<p>${content}</p>`;
  }

  private renderHeading(node: HeadingNode): string {
    const content = this.renderInlineNodes(node.children);
    return `<h${node.level}>${content}</h${node.level}>`;
  }

  private renderQuote(node: QuoteNode): string {
    const content = this.renderInlineNodes(node.children);
    return `<blockquote>${content}</blockquote>`;
  }

  private renderCodeBlock(node: CodeBlockNode): string {
    const content = node.children
      .map((child) => this.escapeHtml(child.text))
      .join("");
    return `<pre><code>${content}</code></pre>`;
  }

  private renderList(node: ListNode): string {
    const tag = node.format === "ordered" ? "ol" : "ul";
    const items = node.children
      .map((item) => this.renderListItem(item))
      .join("");
    return `<${tag}>${items}</${tag}>`;
  }

  private renderListItem(node: ListItemNode): string {
    const content = this.renderInlineNodes(node.children);
    return `<li>${content}</li>`;
  }

  private renderImage(node: ImageNode): string {
    const { image } = node;
    const alt = this.escapeHtml(image.alternativeText || image.name || "");
    const caption = image.caption
      ? `<figcaption>${this.escapeHtml(image.caption)}</figcaption>`
      : "";

    return `
      <figure>
        <img src="${this.escapeHtml(image.url)}" alt="${alt}" />
        ${caption}
      </figure>
    `;
  }

  private renderInlineNodes(nodes: InlineNode[]): string {
    if (!nodes || !Array.isArray(nodes)) {
      return "";
    }
    return nodes.map((node) => this.renderInlineNode(node)).join("");
  }

  private renderInlineNode(node: InlineNode): string {
    if (node.type === "text") {
      return this.renderTextNode(node);
    } else if (node.type === "link") {
      return this.renderLinkNode(node);
    }
    return "";
  }

  private renderTextNode(node: TextNode): string {
    let text = this.escapeHtml(node.text);

    if (node.bold) {
      text = `<strong>${text}</strong>`;
    }
    if (node.italic) {
      text = `<em>${text}</em>`;
    }
    if (node.underline) {
      text = `<u>${text}</u>`;
    }
    if (node.strikethrough) {
      text = `<s>${text}</s>`;
    }
    if (node.code) {
      text = `<code>${text}</code>`;
    }

    return text;
  }

  private renderLinkNode(node: LinkNode): string {
    const content = node.children
      .map((child) => this.renderTextNode(child))
      .join("");
    const url = this.escapeHtml(node.url);
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${content}</a>`;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}
